/**
 * Frame Library - Sammlung von SVG-basierten Foto-Rahmen
 *
 * Kategorien:
 * - polaroid: Klassischer Polaroid-Stil mit weißem Rand und Beschriftungsbereich
 * - circle: Kreisförmige Masken
 * - vintage: Ornamentale, klassische Rahmen
 * - modern: Geometrische, minimalistische Designs
 * - decorative: Verspielte Formen und wellige Kanten
 * - minimal: Einfache Linien und Ränder
 *
 * Alle Rahmen verwenden currentColor für anpassbare Farben
 * und definieren eine imageArea in Prozent-Werten.
 */

import type { FrameElement } from './elementTypes';

// ============================================================================
// POLAROID RAHMEN
// ============================================================================

const polaroidClassic: FrameElement = {
  id: 'frame-polaroid-classic',
  type: 'frame',
  name: 'Polaroid Classic',
  tags: ['polaroid', 'classic', 'retro', 'instant', 'photo'],
  category: 'polaroid',
  frameStyle: 'polaroid',
  aspectRatio: 0.85,
  imageArea: { x: 8, y: 6, width: 84, height: 66 },
  svg: `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
    <!-- Rahmen-Hintergrund -->
    <rect x="0" y="0" width="100" height="100" fill="currentColor" rx="1"/>
    <!-- Schatten-Effekt -->
    <rect x="2" y="2" width="96" height="96" fill="rgba(0,0,0,0.05)" rx="1"/>
    <!-- Bild-Bereich (wird durch Clip-Path maskiert) -->
    <defs>
      <clipPath id="polaroid-classic-clip">
        <rect x="8" y="6" width="84" height="66" rx="0.5"/>
      </clipPath>
    </defs>
    <!-- Placeholder für Bild -->
    <rect x="8" y="6" width="84" height="66" fill="#e5e7eb" clip-path="url(#polaroid-classic-clip)"/>
    <!-- Beschriftungsbereich unten -->
    <rect x="8" y="78" width="84" height="16" fill="transparent"/>
  </svg>`
};

const polaroidWide: FrameElement = {
  id: 'frame-polaroid-wide',
  type: 'frame',
  name: 'Polaroid Breit',
  tags: ['polaroid', 'wide', 'landscape', 'panorama'],
  category: 'polaroid',
  frameStyle: 'polaroid',
  aspectRatio: 1.2,
  imageArea: { x: 6, y: 8, width: 88, height: 60 },
  svg: `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
    <rect x="0" y="0" width="100" height="100" fill="currentColor" rx="1"/>
    <defs>
      <clipPath id="polaroid-wide-clip">
        <rect x="6" y="8" width="88" height="60"/>
      </clipPath>
    </defs>
    <rect x="6" y="8" width="88" height="60" fill="#e5e7eb"/>
    <!-- Unterer Bereich mit Linien für Beschriftung -->
    <line x1="12" y1="80" x2="88" y2="80" stroke="#d1d5db" stroke-width="0.3"/>
    <line x1="12" y1="86" x2="88" y2="86" stroke="#d1d5db" stroke-width="0.3"/>
  </svg>`
};

const polaroidStack: FrameElement = {
  id: 'frame-polaroid-stack',
  type: 'frame',
  name: 'Polaroid Stapel',
  tags: ['polaroid', 'stack', 'layered', 'creative'],
  category: 'polaroid',
  frameStyle: 'polaroid',
  aspectRatio: 0.85,
  imageArea: { x: 12, y: 8, width: 80, height: 62 },
  svg: `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
    <!-- Hinterer Rahmen (gedreht) -->
    <rect x="6" y="4" width="88" height="92" fill="#f3f4f6" rx="1" transform="rotate(-6 50 50)"/>
    <!-- Mittlerer Rahmen (leicht gedreht) -->
    <rect x="6" y="4" width="88" height="92" fill="#f9fafb" rx="1" transform="rotate(3 50 50)"/>
    <!-- Vorderer Rahmen -->
    <rect x="4" y="2" width="92" height="96" fill="currentColor" rx="1"/>
    <defs>
      <clipPath id="polaroid-stack-clip">
        <rect x="12" y="8" width="80" height="62"/>
      </clipPath>
    </defs>
    <rect x="12" y="8" width="80" height="62" fill="#e5e7eb"/>
  </svg>`
};

const polaroidTape: FrameElement = {
  id: 'frame-polaroid-tape',
  type: 'frame',
  name: 'Polaroid mit Klebeband',
  tags: ['polaroid', 'tape', 'scrapbook', 'handmade'],
  category: 'polaroid',
  frameStyle: 'polaroid',
  aspectRatio: 0.85,
  imageArea: { x: 8, y: 10, width: 84, height: 62 },
  svg: `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
    <!-- Klebeband oben -->
    <rect x="30" y="-2" width="40" height="10" fill="#fef3c7" opacity="0.8" transform="rotate(-2 50 3)"/>
    <!-- Hauptrahmen -->
    <rect x="4" y="4" width="92" height="92" fill="currentColor" rx="1"/>
    <defs>
      <clipPath id="polaroid-tape-clip">
        <rect x="8" y="10" width="84" height="62"/>
      </clipPath>
    </defs>
    <rect x="8" y="10" width="84" height="62" fill="#e5e7eb"/>
    <!-- Beschriftungsbereich -->
    <rect x="8" y="78" width="84" height="14" fill="transparent"/>
  </svg>`
};

// ============================================================================
// KREIS RAHMEN
// ============================================================================

const circleSimple: FrameElement = {
  id: 'frame-circle-simple',
  type: 'frame',
  name: 'Kreis Einfach',
  tags: ['circle', 'round', 'simple', 'avatar'],
  category: 'circle',
  frameStyle: 'circle',
  aspectRatio: 1,
  imageArea: { x: 5, y: 5, width: 90, height: 90 },
  svg: `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <clipPath id="circle-simple-clip">
        <circle cx="50" cy="50" r="45"/>
      </clipPath>
    </defs>
    <!-- Äußerer Ring -->
    <circle cx="50" cy="50" r="48" fill="none" stroke="currentColor" stroke-width="4"/>
    <!-- Bild-Bereich -->
    <circle cx="50" cy="50" r="45" fill="#e5e7eb"/>
  </svg>`
};

const circleDouble: FrameElement = {
  id: 'frame-circle-double',
  type: 'frame',
  name: 'Kreis Doppelt',
  tags: ['circle', 'double', 'ring', 'elegant'],
  category: 'circle',
  frameStyle: 'circle',
  aspectRatio: 1,
  imageArea: { x: 10, y: 10, width: 80, height: 80 },
  svg: `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <clipPath id="circle-double-clip">
        <circle cx="50" cy="50" r="40"/>
      </clipPath>
    </defs>
    <!-- Äußerer Ring -->
    <circle cx="50" cy="50" r="48" fill="none" stroke="currentColor" stroke-width="2"/>
    <!-- Innerer Ring -->
    <circle cx="50" cy="50" r="44" fill="none" stroke="currentColor" stroke-width="1"/>
    <!-- Bild-Bereich -->
    <circle cx="50" cy="50" r="40" fill="#e5e7eb"/>
  </svg>`
};

const circleScalloped: FrameElement = {
  id: 'frame-circle-scalloped',
  type: 'frame',
  name: 'Kreis Gewellt',
  tags: ['circle', 'scalloped', 'wavy', 'decorative'],
  category: 'circle',
  frameStyle: 'circle',
  aspectRatio: 1,
  imageArea: { x: 15, y: 15, width: 70, height: 70 },
  svg: `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <clipPath id="circle-scalloped-clip">
        <circle cx="50" cy="50" r="35"/>
      </clipPath>
    </defs>
    <!-- Gewellter Rand -->
    <path d="M50,2
      Q62,2 68,10 Q74,2 82,10 Q90,10 90,18
      Q98,24 98,32 Q98,44 90,50
      Q98,56 98,68 Q98,76 90,82
      Q90,90 82,90 Q76,98 68,90
      Q62,98 50,98
      Q38,98 32,90 Q24,98 18,90
      Q10,90 10,82 Q2,76 2,68
      Q2,56 10,50 Q2,44 2,32
      Q2,24 10,18 Q10,10 18,10
      Q24,2 32,10 Q38,2 50,2 Z"
      fill="currentColor"/>
    <!-- Bild-Bereich -->
    <circle cx="50" cy="50" r="35" fill="#e5e7eb"/>
  </svg>`
};

const circleBrush: FrameElement = {
  id: 'frame-circle-brush',
  type: 'frame',
  name: 'Kreis Pinselstrich',
  tags: ['circle', 'brush', 'artistic', 'paint'],
  category: 'circle',
  frameStyle: 'circle',
  aspectRatio: 1,
  imageArea: { x: 12, y: 12, width: 76, height: 76 },
  svg: `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <clipPath id="circle-brush-clip">
        <circle cx="50" cy="50" r="38"/>
      </clipPath>
    </defs>
    <!-- Pinselstrich-artiger Rand -->
    <path d="M50,4
      C68,3 85,12 92,28 C100,44 98,62 88,76
      C78,92 58,100 40,96 C22,92 8,78 4,60
      C0,42 6,22 20,10 C34,-2 52,3 50,4 Z"
      fill="currentColor" opacity="0.9"/>
    <path d="M50,6
      C66,5 82,14 88,28 C96,42 94,58 86,72
      C76,88 58,96 42,92 C26,88 12,76 8,60
      C4,44 10,26 22,14 C36,2 52,5 50,6 Z"
      fill="currentColor"/>
    <!-- Bild-Bereich -->
    <circle cx="50" cy="50" r="38" fill="#e5e7eb"/>
  </svg>`
};

// ============================================================================
// VINTAGE RAHMEN
// ============================================================================

const vintageOrnate: FrameElement = {
  id: 'frame-vintage-ornate',
  type: 'frame',
  name: 'Vintage Ornament',
  tags: ['vintage', 'ornate', 'baroque', 'elegant', 'classic'],
  category: 'vintage',
  frameStyle: 'vintage',
  aspectRatio: 0.8,
  imageArea: { x: 12, y: 12, width: 76, height: 76 },
  svg: `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
    <!-- Äußerer Rahmen -->
    <rect x="2" y="2" width="96" height="96" fill="none" stroke="currentColor" stroke-width="3" rx="2"/>
    <!-- Ornament-Ecken -->
    <path d="M2,2 Q15,8 8,15 Q2,8 2,2 Z" fill="currentColor"/>
    <path d="M98,2 Q92,8 85,15 Q98,8 98,2 Z" fill="currentColor"/>
    <path d="M2,98 Q8,92 15,85 Q8,98 2,98 Z" fill="currentColor"/>
    <path d="M98,98 Q92,92 85,85 Q92,98 98,98 Z" fill="currentColor"/>
    <!-- Innerer Rahmen -->
    <rect x="8" y="8" width="84" height="84" fill="none" stroke="currentColor" stroke-width="1" rx="1"/>
    <!-- Dekoration oben/unten -->
    <path d="M30,8 Q50,4 70,8" fill="none" stroke="currentColor" stroke-width="1"/>
    <path d="M30,92 Q50,96 70,92" fill="none" stroke="currentColor" stroke-width="1"/>
    <!-- Bild-Bereich -->
    <rect x="12" y="12" width="76" height="76" fill="#e5e7eb"/>
  </svg>`
};

const vintageGold: FrameElement = {
  id: 'frame-vintage-gold',
  type: 'frame',
  name: 'Vintage Gold',
  tags: ['vintage', 'gold', 'luxury', 'museum', 'art'],
  category: 'vintage',
  frameStyle: 'vintage',
  aspectRatio: 0.75,
  imageArea: { x: 15, y: 15, width: 70, height: 70 },
  svg: `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
    <!-- Mehrschichtiger Rahmen -->
    <rect x="0" y="0" width="100" height="100" fill="currentColor" rx="1"/>
    <rect x="4" y="4" width="92" height="92" fill="none" stroke="rgba(255,255,255,0.3)" stroke-width="1"/>
    <rect x="8" y="8" width="84" height="84" fill="none" stroke="rgba(0,0,0,0.2)" stroke-width="2"/>
    <rect x="12" y="12" width="76" height="76" fill="none" stroke="rgba(255,255,255,0.2)" stroke-width="1"/>
    <!-- Eck-Ornamente -->
    <circle cx="8" cy="8" r="3" fill="currentColor" stroke="rgba(255,255,255,0.3)" stroke-width="0.5"/>
    <circle cx="92" cy="8" r="3" fill="currentColor" stroke="rgba(255,255,255,0.3)" stroke-width="0.5"/>
    <circle cx="8" cy="92" r="3" fill="currentColor" stroke="rgba(255,255,255,0.3)" stroke-width="0.5"/>
    <circle cx="92" cy="92" r="3" fill="currentColor" stroke="rgba(255,255,255,0.3)" stroke-width="0.5"/>
    <!-- Bild-Bereich -->
    <rect x="15" y="15" width="70" height="70" fill="#e5e7eb"/>
  </svg>`
};

const vintageFilm: FrameElement = {
  id: 'frame-vintage-film',
  type: 'frame',
  name: 'Filmstreifen',
  tags: ['vintage', 'film', 'cinema', 'movie', 'retro'],
  category: 'vintage',
  frameStyle: 'vintage',
  aspectRatio: 1.5,
  imageArea: { x: 12, y: 18, width: 76, height: 64 },
  svg: `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
    <!-- Filmstreifen Hintergrund -->
    <rect x="0" y="0" width="100" height="100" fill="currentColor"/>
    <!-- Perforationslöcher oben -->
    <rect x="6" y="4" width="6" height="10" rx="1" fill="#1f2937"/>
    <rect x="20" y="4" width="6" height="10" rx="1" fill="#1f2937"/>
    <rect x="34" y="4" width="6" height="10" rx="1" fill="#1f2937"/>
    <rect x="48" y="4" width="6" height="10" rx="1" fill="#1f2937"/>
    <rect x="62" y="4" width="6" height="10" rx="1" fill="#1f2937"/>
    <rect x="76" y="4" width="6" height="10" rx="1" fill="#1f2937"/>
    <rect x="90" y="4" width="6" height="10" rx="1" fill="#1f2937"/>
    <!-- Perforationslöcher unten -->
    <rect x="6" y="86" width="6" height="10" rx="1" fill="#1f2937"/>
    <rect x="20" y="86" width="6" height="10" rx="1" fill="#1f2937"/>
    <rect x="34" y="86" width="6" height="10" rx="1" fill="#1f2937"/>
    <rect x="48" y="86" width="6" height="10" rx="1" fill="#1f2937"/>
    <rect x="62" y="86" width="6" height="10" rx="1" fill="#1f2937"/>
    <rect x="76" y="86" width="6" height="10" rx="1" fill="#1f2937"/>
    <rect x="90" y="86" width="6" height="10" rx="1" fill="#1f2937"/>
    <!-- Bild-Bereich -->
    <rect x="12" y="18" width="76" height="64" fill="#e5e7eb"/>
  </svg>`
};

// ============================================================================
// MODERN RAHMEN
// ============================================================================

const modernThin: FrameElement = {
  id: 'frame-modern-thin',
  type: 'frame',
  name: 'Modern Dünn',
  tags: ['modern', 'thin', 'minimal', 'clean', 'simple'],
  category: 'modern',
  frameStyle: 'modern',
  aspectRatio: 1,
  imageArea: { x: 4, y: 4, width: 92, height: 92 },
  svg: `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
    <!-- Dünner Rahmen -->
    <rect x="2" y="2" width="96" height="96" fill="none" stroke="currentColor" stroke-width="2"/>
    <!-- Bild-Bereich -->
    <rect x="4" y="4" width="92" height="92" fill="#e5e7eb"/>
  </svg>`
};

const modernFloating: FrameElement = {
  id: 'frame-modern-floating',
  type: 'frame',
  name: 'Modern Schwebend',
  tags: ['modern', 'floating', 'shadow', '3d'],
  category: 'modern',
  frameStyle: 'modern',
  aspectRatio: 1,
  imageArea: { x: 6, y: 6, width: 82, height: 82 },
  svg: `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <filter id="modern-floating-shadow" x="-20%" y="-20%" width="140%" height="140%">
        <feDropShadow dx="4" dy="4" stdDeviation="3" flood-opacity="0.2"/>
      </filter>
    </defs>
    <!-- Haupt-Rahmen mit Schatten -->
    <rect x="4" y="4" width="86" height="86" fill="currentColor" filter="url(#modern-floating-shadow)"/>
    <!-- Bild-Bereich -->
    <rect x="6" y="6" width="82" height="82" fill="#e5e7eb"/>
  </svg>`
};

const modernSplit: FrameElement = {
  id: 'frame-modern-split',
  type: 'frame',
  name: 'Modern Geteilt',
  tags: ['modern', 'split', 'asymmetric', 'creative'],
  category: 'modern',
  frameStyle: 'modern',
  aspectRatio: 1,
  imageArea: { x: 8, y: 8, width: 84, height: 84 },
  svg: `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
    <!-- Geteilter Rahmen -->
    <rect x="0" y="0" width="50" height="4" fill="currentColor"/>
    <rect x="0" y="0" width="4" height="50" fill="currentColor"/>
    <rect x="50" y="96" width="50" height="4" fill="currentColor"/>
    <rect x="96" y="50" width="4" height="50" fill="currentColor"/>
    <!-- Bild-Bereich -->
    <rect x="8" y="8" width="84" height="84" fill="#e5e7eb"/>
  </svg>`
};

const modernGeo: FrameElement = {
  id: 'frame-modern-geo',
  type: 'frame',
  name: 'Modern Geometrisch',
  tags: ['modern', 'geometric', 'hexagon', 'shape'],
  category: 'modern',
  frameStyle: 'modern',
  aspectRatio: 1,
  imageArea: { x: 12, y: 15, width: 76, height: 70 },
  svg: `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <clipPath id="modern-geo-clip">
        <polygon points="50,5 95,27.5 95,72.5 50,95 5,72.5 5,27.5"/>
      </clipPath>
    </defs>
    <!-- Hexagon Rahmen -->
    <polygon points="50,2 98,26 98,74 50,98 2,74 2,26" fill="none" stroke="currentColor" stroke-width="3"/>
    <!-- Bild-Bereich -->
    <polygon points="50,8 92,28 92,72 50,92 8,72 8,28" fill="#e5e7eb"/>
  </svg>`
};

const modernCorners: FrameElement = {
  id: 'frame-modern-corners',
  type: 'frame',
  name: 'Modern Ecken',
  tags: ['modern', 'corners', 'bracket', 'minimal'],
  category: 'modern',
  frameStyle: 'modern',
  aspectRatio: 1,
  imageArea: { x: 5, y: 5, width: 90, height: 90 },
  svg: `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
    <!-- Nur Ecken als Rahmen -->
    <path d="M2,20 L2,2 L20,2" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"/>
    <path d="M80,2 L98,2 L98,20" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"/>
    <path d="M98,80 L98,98 L80,98" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"/>
    <path d="M20,98 L2,98 L2,80" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"/>
    <!-- Bild-Bereich -->
    <rect x="5" y="5" width="90" height="90" fill="#e5e7eb"/>
  </svg>`
};

// ============================================================================
// DEKORATIVE RAHMEN
// ============================================================================

const decorativeWavy: FrameElement = {
  id: 'frame-decorative-wavy',
  type: 'frame',
  name: 'Wellig',
  tags: ['decorative', 'wavy', 'fun', 'playful'],
  category: 'decorative',
  frameStyle: 'decorative',
  aspectRatio: 1,
  imageArea: { x: 10, y: 10, width: 80, height: 80 },
  svg: `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <clipPath id="decorative-wavy-clip">
        <rect x="10" y="10" width="80" height="80"/>
      </clipPath>
    </defs>
    <!-- Welliger Rahmen -->
    <path d="M10,0 Q20,-5 30,0 Q40,5 50,0 Q60,-5 70,0 Q80,5 90,0 L100,0
             L100,10 Q105,20 100,30 Q95,40 100,50 Q105,60 100,70 Q95,80 100,90 L100,100
             L90,100 Q80,105 70,100 Q60,95 50,100 Q40,105 30,100 Q20,95 10,100 L0,100
             L0,90 Q-5,80 0,70 Q5,60 0,50 Q-5,40 0,30 Q5,20 0,10 L0,0 Z"
          fill="currentColor"/>
    <!-- Bild-Bereich -->
    <rect x="10" y="10" width="80" height="80" fill="#e5e7eb"/>
  </svg>`
};

const decorativeStamp: FrameElement = {
  id: 'frame-decorative-stamp',
  type: 'frame',
  name: 'Briefmarke',
  tags: ['decorative', 'stamp', 'postage', 'mail'],
  category: 'decorative',
  frameStyle: 'decorative',
  aspectRatio: 0.8,
  imageArea: { x: 10, y: 10, width: 80, height: 80 },
  svg: `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <clipPath id="decorative-stamp-clip">
        <rect x="10" y="10" width="80" height="80"/>
      </clipPath>
    </defs>
    <!-- Gezackter Briefmarken-Rand -->
    <path d="M0,8
      ${Array.from({length: 12}, (_, i) => `A4,4 0 0,1 ${(i+1)*8},8`).join(' ')}
      ${Array.from({length: 12}, (_, i) => `A4,4 0 0,1 96,${8+(i+1)*7}`).join(' ')}
      ${Array.from({length: 12}, (_, i) => `A4,4 0 0,1 ${96-(i+1)*8},92`).join(' ')}
      ${Array.from({length: 12}, (_, i) => `A4,4 0 0,1 4,${92-(i+1)*7}`).join(' ')}
      Z" fill="currentColor"/>
    <!-- Innerer Rahmen -->
    <rect x="8" y="8" width="84" height="84" fill="none" stroke="rgba(0,0,0,0.1)" stroke-width="1"/>
    <!-- Bild-Bereich -->
    <rect x="10" y="10" width="80" height="80" fill="#e5e7eb"/>
  </svg>`
};

const decorativeCloud: FrameElement = {
  id: 'frame-decorative-cloud',
  type: 'frame',
  name: 'Wolke',
  tags: ['decorative', 'cloud', 'soft', 'dreamy'],
  category: 'decorative',
  frameStyle: 'decorative',
  aspectRatio: 1.2,
  imageArea: { x: 15, y: 20, width: 70, height: 55 },
  svg: `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <clipPath id="decorative-cloud-clip">
        <ellipse cx="50" cy="50" rx="35" ry="28"/>
      </clipPath>
    </defs>
    <!-- Wolken-Form -->
    <path d="M25,70
      Q5,70 10,55 Q0,45 15,35 Q10,20 30,20
      Q35,5 55,10 Q75,0 85,20 Q100,25 95,45
      Q105,60 85,70 Q90,85 70,85 Q55,95 40,85 Q20,90 25,70 Z"
      fill="currentColor"/>
    <!-- Bild-Bereich -->
    <ellipse cx="50" cy="50" rx="35" ry="28" fill="#e5e7eb"/>
  </svg>`
};

const decorativeHeart: FrameElement = {
  id: 'frame-decorative-heart',
  type: 'frame',
  name: 'Herz',
  tags: ['decorative', 'heart', 'love', 'romantic'],
  category: 'decorative',
  frameStyle: 'decorative',
  aspectRatio: 1,
  imageArea: { x: 18, y: 22, width: 64, height: 52 },
  svg: `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <clipPath id="decorative-heart-clip">
        <path d="M50,85 C20,65 5,40 20,25 C35,10 50,20 50,35 C50,20 65,10 80,25 C95,40 80,65 50,85 Z"/>
      </clipPath>
    </defs>
    <!-- Herz-Rahmen -->
    <path d="M50,90 C15,68 0,38 18,20 C36,2 50,15 50,32 C50,15 64,2 82,20 C100,38 85,68 50,90 Z"
      fill="currentColor"/>
    <!-- Bild-Bereich -->
    <path d="M50,82 C22,62 8,38 22,24 C36,10 50,20 50,35 C50,20 64,10 78,24 C92,38 78,62 50,82 Z"
      fill="#e5e7eb"/>
  </svg>`
};

// ============================================================================
// MINIMAL RAHMEN
// ============================================================================

const minimalLine: FrameElement = {
  id: 'frame-minimal-line',
  type: 'frame',
  name: 'Minimal Linie',
  tags: ['minimal', 'line', 'simple', 'clean'],
  category: 'minimal',
  frameStyle: 'minimal',
  aspectRatio: 1,
  imageArea: { x: 2, y: 2, width: 96, height: 96 },
  svg: `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
    <!-- Nur eine untere Linie -->
    <line x1="0" y1="100" x2="100" y2="100" stroke="currentColor" stroke-width="2"/>
    <!-- Bild-Bereich -->
    <rect x="2" y="2" width="96" height="96" fill="#e5e7eb"/>
  </svg>`
};

const minimalDashed: FrameElement = {
  id: 'frame-minimal-dashed',
  type: 'frame',
  name: 'Minimal Gestrichelt',
  tags: ['minimal', 'dashed', 'dotted', 'simple'],
  category: 'minimal',
  frameStyle: 'minimal',
  aspectRatio: 1,
  imageArea: { x: 4, y: 4, width: 92, height: 92 },
  svg: `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
    <!-- Gestrichelter Rahmen -->
    <rect x="2" y="2" width="96" height="96" fill="none"
      stroke="currentColor" stroke-width="1" stroke-dasharray="6 4"/>
    <!-- Bild-Bereich -->
    <rect x="4" y="4" width="92" height="92" fill="#e5e7eb"/>
  </svg>`
};

const minimalRounded: FrameElement = {
  id: 'frame-minimal-rounded',
  type: 'frame',
  name: 'Minimal Abgerundet',
  tags: ['minimal', 'rounded', 'soft', 'modern'],
  category: 'minimal',
  frameStyle: 'minimal',
  aspectRatio: 1,
  imageArea: { x: 3, y: 3, width: 94, height: 94 },
  svg: `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
    <!-- Abgerundeter Rahmen -->
    <rect x="1" y="1" width="98" height="98" fill="none"
      stroke="currentColor" stroke-width="2" rx="12"/>
    <!-- Bild-Bereich -->
    <rect x="3" y="3" width="94" height="94" fill="#e5e7eb" rx="10"/>
  </svg>`
};

const minimalShadow: FrameElement = {
  id: 'frame-minimal-shadow',
  type: 'frame',
  name: 'Minimal Schatten',
  tags: ['minimal', 'shadow', 'elevated', 'clean'],
  category: 'minimal',
  frameStyle: 'minimal',
  aspectRatio: 1,
  imageArea: { x: 2, y: 2, width: 90, height: 90 },
  svg: `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <filter id="minimal-shadow-filter" x="-10%" y="-10%" width="130%" height="130%">
        <feDropShadow dx="3" dy="3" stdDeviation="2" flood-opacity="0.15"/>
      </filter>
    </defs>
    <!-- Rahmen mit Schatten -->
    <rect x="2" y="2" width="90" height="90" fill="currentColor" filter="url(#minimal-shadow-filter)"/>
    <!-- Bild-Bereich (leicht eingerückt) -->
    <rect x="4" y="4" width="86" height="86" fill="#e5e7eb"/>
  </svg>`
};

// ============================================================================
// EXPORT - Alle Rahmen als Bibliothek
// ============================================================================

export const FRAME_LIBRARY: FrameElement[] = [
  // Polaroid
  polaroidClassic,
  polaroidWide,
  polaroidStack,
  polaroidTape,
  // Kreis
  circleSimple,
  circleDouble,
  circleScalloped,
  circleBrush,
  // Vintage
  vintageOrnate,
  vintageGold,
  vintageFilm,
  // Modern
  modernThin,
  modernFloating,
  modernSplit,
  modernGeo,
  modernCorners,
  // Dekorativ
  decorativeWavy,
  decorativeStamp,
  decorativeCloud,
  decorativeHeart,
  // Minimal
  minimalLine,
  minimalDashed,
  minimalRounded,
  minimalShadow,
];

// Kategorien für UI-Gruppierung
export const FRAME_CATEGORIES = [
  { id: 'polaroid', label: 'Polaroid', description: 'Klassische Sofortbild-Rahmen' },
  { id: 'circle', label: 'Kreis', description: 'Runde Rahmen und Masken' },
  { id: 'vintage', label: 'Vintage', description: 'Klassische, ornamentale Rahmen' },
  { id: 'modern', label: 'Modern', description: 'Geometrische, minimalistische Designs' },
  { id: 'decorative', label: 'Dekorativ', description: 'Verspielte Formen und Effekte' },
  { id: 'minimal', label: 'Minimal', description: 'Einfache Linien und Ränder' },
] as const;

// Hilfsfunktion: Rahmen nach Kategorie filtern
export function getFramesByCategory(category: string): FrameElement[] {
  return FRAME_LIBRARY.filter(frame => frame.category === category);
}

// Hilfsfunktion: Rahmen nach Tags durchsuchen
export function searchFrames(query: string): FrameElement[] {
  const lowerQuery = query.toLowerCase();
  return FRAME_LIBRARY.filter(frame =>
    frame.name.toLowerCase().includes(lowerQuery) ||
    frame.tags.some(tag => tag.toLowerCase().includes(lowerQuery))
  );
}
